import { useMutation, useSuspenseQuery } from "@apollo/client";
import {
  Button,
  DataTable,
  DataTableColumnHeader,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  PrimaryFullScreenSpinner,
  toast,
} from "@repo/ui";
import { formatRate, NotFoundError, neverFunc } from "@repo/utils";
import { createFileRoute } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { compareAsc } from "date-fns";
import Decimal from "decimal.js";
import { MoreVertical } from "lucide-react";
import { DateTime } from "luxon";
import { Suspense, useState } from "react";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { UpdateRateModal } from "~/components/Product/UpdateRateModal";
import { graphql } from "~/gql/client";
import type {
  AdminBankAccountProductRateHistoryFragment,
  AdminFundProductRateHistoryFragment,
  AdminTermDepositRateHistoryFragment,
} from "~/gql/client/graphql";
import { opsHead } from "~/head";

export const Route = createFileRoute(
  "/__auth/products_/$productId_/rate-history",
)({
  head: () => opsHead("Product Rate History"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

graphql(`
fragment AdminBankAccountProductRateHistory on BankAccountRate {
    id
    rate
    updatedAt
    effectiveFromInclusive
    updatedAt
    updatedBy {
        id
        email
    }
    tiers {
        id
        rate
        minBalanceInclusive
        maxBalanceExclusive
    }
}

fragment AdminFundProductRateHistory on FundRate {
    id
    rate
    effectiveFromInclusive
    effectiveToExclusive
    updatedAt
    updatedBy {
        id
        email
    }
}

fragment AdminTermDepositRateHistory on TermDepositRateQuote {
    id
    groupId
    rate
    updatedAt
    effectiveAt
    updatedBy {
        id
        email
    }
}
`);

type Row =
  | AdminBankAccountProductRateHistoryFragment
  | AdminFundProductRateHistoryFragment
  | AdminTermDepositRateHistoryFragment;

function View() {
  const { productId } = Route.useParams();
  const { data } = useSuspenseQuery(
    graphql(`
      query AdminProductRateHistoryPage($productId: ID!) {
          product(id: $productId) {
              id
              name
              ...on BankAccountProduct {
                  rateHistory {
                      ...AdminBankAccountProductRateHistory
                  }
              }
              ...on FundProduct {
                  rates {
                      ...AdminFundProductRateHistory
                  }
              }
              ...on TermDepositProduct {
                  rateQuoteHistory {
                      ...AdminTermDepositRateHistory
                  }
              }
              ...UpdateRateModalProduct
          }
      }
    `),
    { variables: { productId } },
  );

  const product = data.product;
  if (!product) {
    throw new NotFoundError();
  }

  const [updatingRow, setUpdatingRow] = useState<Row | null>(null);
  const [deletingRow, setDeletingRow] = useState<Row | null>(null);

  let rows: Row[];
  switch (product.__typename) {
    case "BankAccountProduct":
      rows = [...product.rateHistory].sort(
        (a, b) =>
          b.effectiveFromInclusive.getTime() -
          a.effectiveFromInclusive.getTime(),
      );
      break;
    case "FundProduct":
      rows = [...product.rates].sort(
        (a, b) =>
          b.effectiveToExclusive.getTime() - a.effectiveToExclusive.getTime(),
      );
      break;
    case "TermDepositProduct":
      rows = [...product.rateQuoteHistory].sort(
        (a, b) => b.effectiveAt.getTime() - a.effectiveAt.getTime(),
      );
      break;
    default:
      throw neverFunc(product);
  }

  const columns: ColumnDef<Row>[] = [];

  columns.push({
    id: "at",
    header: (row) => (
      <DataTableColumnHeader
        column={row.column}
        title={
          product.__typename === "BankAccountProduct"
            ? "Effective from"
            : "As of"
        }
      />
    ),
    accessorFn: (row) => {
      switch (row.__typename) {
        case "BankAccountRate":
          return DateTime.fromJSDate(row.effectiveFromInclusive).toLocaleString(
            DateTime.DATE_FULL,
          );
        case "FundRate":
          return DateTime.fromJSDate(row.effectiveToExclusive).toLocaleString(
            DateTime.DATE_FULL,
          );
        case "TermDepositRateQuote":
          return DateTime.fromJSDate(row.effectiveAt).toLocaleString(
            DateTime.DATE_FULL,
          );
        default:
          throw neverFunc(row);
      }
    },
    sortingFn: (a, b) => {
      switch (product.__typename) {
        case "BankAccountProduct":
          if (
            a.original.__typename !== "BankAccountRate" ||
            b.original.__typename !== "BankAccountRate"
          ) {
            throw new Error('expected rows to be of kind "bank_account');
          }
          return compareAsc(
            a.original.effectiveFromInclusive,
            b.original.effectiveFromInclusive,
          );
        case "FundProduct":
          if (
            a.original.__typename !== "FundRate" ||
            b.original.__typename !== "FundRate"
          ) {
            throw new Error('expected rows to be of kind "fund');
          }
          return compareAsc(
            a.original.effectiveToExclusive,
            b.original.effectiveToExclusive,
          );
        case "TermDepositProduct":
          if (
            a.original.__typename !== "TermDepositRateQuote" ||
            b.original.__typename !== "TermDepositRateQuote"
          ) {
            throw new Error('expected rows to be of kind "term_deposit');
          }
          return compareAsc(a.original.effectiveAt, b.original.effectiveAt);
        default:
          return neverFunc(product);
      }
    },
  });

  switch (product.__typename) {
    case "FundProduct":
    case "TermDepositProduct":
      columns.push({
        id: "rate",
        header: (row) => (
          <DataTableColumnHeader column={row.column} title="Rate" />
        ),
        accessorFn: (row) => formatRate(row.rate),
      });
      break;

    case "BankAccountProduct": {
      if (product.rateHistory.some((rh) => rh.tiers.length > 0)) {
        let maxBalanceAcrossTiers = new Decimal("0");
        let minBalanceAcrossTiers = new Decimal("1");
        // Create a column for each tier
        const tierColumns = new Map<
          string,
          { minBalanceInclusive: Decimal; maxBalanceExclusive: Decimal }
        >();

        for (const tier of product.rateHistory.flatMap((rh) => rh.tiers)) {
          tierColumns.set(
            `${tier.minBalanceInclusive}:${tier.maxBalanceExclusive}`,
            {
              minBalanceInclusive: tier.minBalanceInclusive,
              maxBalanceExclusive: tier.maxBalanceExclusive,
            },
          );
          if (tier.maxBalanceExclusive.gt(maxBalanceAcrossTiers)) {
            maxBalanceAcrossTiers = tier.maxBalanceExclusive;
          }
          if (tier.minBalanceInclusive.lt(minBalanceAcrossTiers)) {
            minBalanceAcrossTiers = tier.minBalanceInclusive;
          }
        }

        // This code only supports flattening tiers into columns for products like macquaries CMA accelerator:
        // 0-2m: 4%
        // 2-10m: 3%
        // 10m+: 2%
        // Adding an assertion here so we don't show incorrect/confusing data.
        if (!minBalanceAcrossTiers.eq(new Decimal("0"))) {
          throw new Error("unable to flatten tiers into columns");
        }

        columns.push(
          ...Array.from(tierColumns.values())
            .sort((a, b) => a.minBalanceInclusive.cmp(b.minBalanceInclusive))
            .map<ColumnDef<Row>>((tierColumn, i) => ({
              id: `tier-${i}`,
              header: (row) => (
                <DataTableColumnHeader
                  column={row.column}
                  title={`$${
                    tierColumn.minBalanceInclusive.isZero()
                      ? 0
                      : tierColumn.minBalanceInclusive.div(1_000_000).toDP(2) +
                        "m"
                  } - $${tierColumn.maxBalanceExclusive
                    .div(1_000_000)
                    .toDP(2)}m`}
                />
              ),
              accessorFn: (row) => {
                if (row.__typename !== "BankAccountRate") {
                  throw new Error('expected row kind to be "bank_account');
                }
                const rateTier = row.tiers.find(
                  (t) =>
                    t.minBalanceInclusive.eq(tierColumn.minBalanceInclusive) &&
                    t.maxBalanceExclusive.eq(tierColumn.maxBalanceExclusive),
                );
                if (rateTier) {
                  return formatRate(rateTier.rate);
                }
              },
            })),
          {
            id: "tier-max",
            header: (row) => (
              <DataTableColumnHeader
                column={row.column}
                title={`$${maxBalanceAcrossTiers.div(1_000_000).toDP(2)}m+`}
              />
            ),
            accessorFn: (row) => formatRate(row.rate),
          },
        );
      } else {
        columns.push({
          id: "rate",
          header: (row) => (
            <DataTableColumnHeader column={row.column} title="Rate" />
          ),
          accessorFn: (row) => formatRate(row.rate),
        });
      }
      break;
    }
    default:
      neverFunc(product);
  }

  columns.push({
    id: "updatedBy",
    accessorFn(row) {
      return row.updatedBy?.email ?? "-";
    },
    header: (row) => (
      <DataTableColumnHeader column={row.column} title="Updated by" />
    ),
  });

  columns.push({
    id: "updatedAt",
    accessorFn: (row) =>
      DateTime.fromJSDate(row.updatedAt).toLocaleString(DateTime.DATE_FULL),
    header: (row) => (
      <DataTableColumnHeader column={row.column} title="Last updated" />
    ),
  });

  columns.push({
    id: "actions",
    cell: ({ row }) => (
      <div className="flex flex-row items-center justify-end">
        <Button
          variant="outline"
          onClick={() => {
            setUpdatingRow(row.original);
          }}
        >
          Edit rate
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="size-8 p-0 hover:bg-secondary">
              <MoreVertical className="size-4" />
              <span className="sr-only">Open menu</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setDeletingRow(row.original)}>
              <div className="p-2 text-danger">Delete entry</div>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    ),
  });

  const [deleteBankAccountRate, { loading: deleteBankAccountRateLoading }] =
    useMutation(
      graphql(`
      mutation DeleteBankAccountRate($input: DeleteBankAccountRateInput!) {
          deleteBankAccountRate(input: $input) {
              bankAccountProduct {
                  id
                  rate {
                      id
                  }
                  rateHistory {
                      id
                  }
              }
          }
      }
    `),
    );

  const [deleteFundRate, { loading: deleteFundRateLoading }] = useMutation(
    graphql(`
      mutation DeleteFundRate($input: DeleteFundRateInput!) {
          deleteFundRate(input: $input) {
              fundProduct {
                  id
                  rates {
                      id
                  }
              }
          }
      }
    `),
  );

  const [deleteTermDepositRate, { loading: deleteTermDepositRateLoading }] =
    useMutation(
      graphql(`
      mutation DeleteTermDepositRate($input: DeleteTermDepositRateQuoteGroupInput!) {
          deleteTermDepositRateQuoteGroup(input: $input) {
              termDepositProduct {
                  id
                  rateQuoteHistory {
                      id
                  }
                  rateQuotes {
                      id
                  }
              }
          }
      }
    `),
    );

  return (
    <AdminPageContainer>
      <AdminPageHeader title="Rate history" subtitle={product.name} />
      <DataTable
        data={rows}
        columns={columns}
        initialState={{
          pagination: {
            pageSize: 50,
          },
          sorting: [
            {
              id: "at",
              desc: true,
            },
          ],
        }}
      />
      <Dialog
        open={!!deletingRow}
        modal
        onOpenChange={() => setDeletingRow(null)}
      >
        <DialogContent className="max-w-xl">
          <DialogHeader className="flex flex-row items-center justify-between">
            <h2 className="font-semibold text-xl">Deleting rate entry</h2>
          </DialogHeader>
          <p>
            This action is not reversible. Are you sure you want to delete this
            rate entry?
          </p>
          <DialogFooter>
            <div className="mt-4 flex w-full flex-row justify-end gap-2">
              <DialogClose asChild>
                <Button variant="outline">Cancel</Button>
              </DialogClose>
              <Button
                type="submit"
                variant="destructive"
                loading={
                  deleteBankAccountRateLoading ||
                  deleteFundRateLoading ||
                  deleteTermDepositRateLoading
                }
                onClick={async () => {
                  switch (deletingRow?.__typename) {
                    case "BankAccountRate":
                      await deleteBankAccountRate({
                        variables: { input: { id: deletingRow.id } },
                      });
                      break;
                    case "FundRate":
                      await deleteFundRate({
                        variables: { input: { id: deletingRow.id } },
                      });
                      break;
                    case "TermDepositRateQuote":
                      await deleteTermDepositRate({
                        variables: { input: { groupId: deletingRow.groupId } },
                      });
                      break;
                  }
                  setDeletingRow(null);
                  toast({
                    kind: "success",
                    autoHide: true,
                    title: "Rate deleted",
                  });
                }}
              >
                Delete
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <UpdateRateModal
        open={!!updatingRow}
        product={product}
        comparisonRateId={(() => {
          if (!updatingRow) {
            return null;
          }
          return updatingRow.__typename === "TermDepositRateQuote"
            ? updatingRow.groupId
            : updatingRow.id;
        })()}
        onOpenChange={() => setUpdatingRow(null)}
        key={(() => {
          if (!updatingRow) {
            return "empty";
          }
          return updatingRow.__typename === "TermDepositRateQuote"
            ? updatingRow.groupId
            : updatingRow.id;
        })()}
      />
    </AdminPageContainer>
  );
}
